"use client";

import * as React from "react";

import { apiSend } from "@/lib/api/client";
import { patchTaskContract } from "@/lib/api/contracts/tasks";
import type { TaskSnapshot } from "@/lib/tasks/queries";

/**
 * Shared `PATCH /api/tasks/{id}` wrapper.
 *
 * Four surfaces edit tasks today (task board card, agent card, detail drawer,
 * detail page) and all of them used to roll their own
 * fetch+error pattern. This hook centralises the request shape, the JSON
 * error parsing, and the success/error callbacks so adding a field to the
 * PATCH body only needs to happen here.
 *
 * Body is intentionally loose (`Record<string, unknown>`) — the server
 * route validates each field. Callers know their own shape; the hook just
 * forwards bytes.
 */

export type TaskPatchBody = Record<string, unknown>;

export type UseTaskPatchOptions = {
  /** Called after a 2xx response. */
  onSuccess?: (snapshot: TaskSnapshot) => void;
  /** Called with the server's `message` (or a generic fallback) on a 4xx/5xx. */
  onError?: (message: string) => void;
};

/**
 * Returns a stable `patch(body)` callback. Resolves the updated authoritative
 * task snapshot on success, or `null` on failure after invoking `onError`.
 */
export function useTaskPatch(
  taskId: string,
  options?: UseTaskPatchOptions,
): (body: TaskPatchBody) => Promise<TaskSnapshot | null> {
  const onSuccess = options?.onSuccess;
  const onError = options?.onError;

  return React.useCallback(
    async (body: TaskPatchBody): Promise<TaskSnapshot | null> => {
      const result = await apiSend(patchTaskContract, {
        params: { id: taskId },
        body,
      });
      if (!result.ok) {
        onError?.(result.error.message || "Could not save change.");
        return null;
      }
      const snapshot = result.data as TaskSnapshot;
      onSuccess?.(snapshot);
      return snapshot;
    },
    [taskId, onSuccess, onError],
  );
}
